import { useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, getDay, isSameMonth, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale';
import type { Appointment } from '../../types/appointment.types';
import type { CalendarEvent } from '@/lib/utils/calendarUtils';
import { DayPopup } from '../DayPopup';
import { BlockTimeModal } from '../BlockTimeModal';
import { BlockedTimeModals } from '../BlockedTimeModals';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarEvent as CalendarEventComponent } from './CalendarEvent';
import { useCalendarUI } from './useCalendarUI';
import { StanLoader } from '@/components';

const locales = {
    'en-US': enUS,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

interface AppointmentsCalendarProps {
    events: CalendarEvent[];
    selectedDate: Date;
    isLoading?: boolean;
    isError?: boolean;
    onDateNavigate: (date: Date) => void;
    onDateSelect?: (date: Date) => void;
    onAppointmentClick?: (appointment: Appointment) => void;
    onBlockTime: (params: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
    }) => Promise<void>;
    onDeleteBlockedTime?: (blockedTimeId: string) => Promise<void>;
    isDeleting?: boolean;
    getAppointmentsForDay: (date: Date) => Appointment[];
    viewMode?: 'calendar' | 'list';
    onViewModeChange?: (mode: 'calendar' | 'list') => void;
}

export function AppointmentsCalendar({
    events,
    selectedDate,
    isLoading,
    isError,
    onDateNavigate,
    onDateSelect,
    onAppointmentClick,
    onBlockTime,
    onDeleteBlockedTime,
    isDeleting = false,
    getAppointmentsForDay,
    viewMode,
    onViewModeChange,
}: AppointmentsCalendarProps) {
    const {
        selectedDay,
        isDayPopupOpen,
        popupPosition,
        isBlockTimeModalOpen,
        handleSelectEvent,
        handleSelectSlot,
        closeDayPopup,
        openBlockTimeModal,
        closeBlockTimeModal,
    } = useCalendarUI();

    const [selectedBlockedTime, setSelectedBlockedTime] = useState<Appointment | null>(null);

    // Handle clicking on events - opens day popup for that date
    const handleEventClick = (event: CalendarEvent, e: React.SyntheticEvent<HTMLElement>) => {
        // Ignore clicks on events outside the currently viewed month
        if (!isSameMonth(event.start, selectedDate)) {
            return;
        }

        handleSelectEvent(event, e);
    };

    // Handle clicking on empty cells - opens day popup for that date
    const handleSlotClick = (slotInfo: {
        start: Date;
        box?: { x: number; y: number; clientX: number; clientY: number };
    }) => {
        // Ignore clicks on dates outside the currently viewed month
        if (!isSameMonth(slotInfo.start, selectedDate)) {
            return;
        }

        handleSelectSlot(slotInfo);
        if (onDateSelect) {
            onDateSelect(slotInfo.start);
        }
    };

    const handleBlockedTimeClick = (appointment: Appointment) => {
        setSelectedBlockedTime(appointment);
    };

    const handleCloseBlockedTimeModals = () => {
        setSelectedBlockedTime(null);
    };

    return (
        <>
            <div className='relative'>
                <div className={isLoading ? '[&_.rbc-month-view]:opacity-0' : ''}>
                    <Calendar
                        localizer={localizer}
                        events={events}
                        startAccessor='start'
                        endAccessor='end'
                        onSelectEvent={handleEventClick}
                        onSelectSlot={handleSlotClick}
                        selectable
                        longPressThreshold={50}
                        views={['month']}
                        defaultView='month'
                        date={selectedDate}
                        onNavigate={onDateNavigate}
                        components={{
                            event: CalendarEventComponent,
                            toolbar: props => (
                                <CalendarToolbar {...props} viewMode={viewMode} onViewModeChange={onViewModeChange} />
                            ),
                        }}
                    />
                </div>

                {isLoading && (
                    <div className='absolute inset-x-0 top-50 flex items-center justify-center'>
                        <StanLoader />
                    </div>
                )}

                {isError && (
                    <div className='absolute inset-0 top-14 flex items-center justify-center'>
                        <p className='text-negative-dark'>Failed to load appointments</p>
                    </div>
                )}
            </div>

            {selectedDay && (
                <DayPopup
                    isOpen={isDayPopupOpen}
                    onClose={closeDayPopup}
                    date={selectedDay}
                    appointments={getAppointmentsForDay(selectedDay)}
                    onOpenBlockTimeModal={openBlockTimeModal}
                    onAppointmentClick={onAppointmentClick}
                    onBlockedTimeClick={handleBlockedTimeClick}
                    position={popupPosition}
                />
            )}

            {selectedDay && (
                <BlockTimeModal
                    isOpen={isBlockTimeModalOpen}
                    onClose={closeBlockTimeModal}
                    selectedDate={selectedDay}
                    onSave={onBlockTime}
                />
            )}

            {selectedBlockedTime && onDeleteBlockedTime && (
                <BlockedTimeModals
                    selectedBlockedTime={selectedBlockedTime}
                    onClose={handleCloseBlockedTimeModals}
                    onSaveBlockTime={onBlockTime}
                    onDeleteBlockTime={onDeleteBlockedTime}
                    isDeleting={isDeleting}
                />
            )}
        </>
    );
}
